<template>
  <Card class="mb-4 selected-raws-component">
    <template #header>
      <div class="card-header-custom">
        <h6 class="mb-0 text-primary font-semibold">
          <i class="pi pi-check-square me-2"></i>
          Selected RAW Files
        </h6>
        <Badge :value="selectedFiles.length" severity="info" />
      </div>
    </template>
    
    <template #content>
      <div v-if="selectedFiles.length === 0" class="text-center py-4">
        <i class="pi pi-image text-4xl text-gray-400 mb-3"></i>
        <p class="text-gray-500 m-0">No RAW files selected</p>
      </div>
      
      <div v-else class="space-y-2 p-raw-body">
        <div v-for="file in selectedFiles" :key="file.path" 
             class="raw-select-row align-items-center p-2 border-round surface-border border-1 bg-gray-50">
          <!-- Thumbnail or icon -->
          <div class="file-icon-container me-2">
            <img v-if="isRawFile(file)"
                 :src="getThumbnailUrlWithAuth(file.path)"
                 class="file-thumbnail-small"
                 @error="onThumbnailError($event, file)"
                 @load="onThumbnailLoad"
                 :title="'Preview of ' + file.name">
            <i :class="[getFileIcon(file), getFileIconClass(file)]"
               class="fallback-icon"
               style="display: none;"></i>
          </div>
          <span class="flex-grow-1 text-sm font-medium">{{ file.name }}</span>
          <Button 
            @click="removeFile(file)" 
            icon="pi pi-times"
            severity="danger"
            text
            rounded
            size="small"
            class="ml-2" />
        </div>
      </div>
      <div class="d-flex justify-content-center mt-3">
        <Button 
            @click="processFiles" 
            :disabled="loading || selectedFiles.length === 0"
            :loading="loading"
            icon="pi pi-play"
            label="Process Selected RAW Files"
            severity="primary" />
      </div>
    </template>
  </Card>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SelectedFiles',
  props: {
    selectedFiles: Array,
    loading: Boolean
  },
  emits: ['remove-file', 'process-files'],
  
  methods: {
    removeFile(file) {
      this.$emit('remove-file', file)
    },
    
    processFiles() {
      this.$emit('process-files')
    },

    getApiBaseUrl() {
      // For production, use the deployed API endpoint with /api prefix
      if (process.env.NODE_ENV === 'production') {
        return `${window.location.protocol}//${window.location.hostname}/api`
      }

      // For development, always use localhost:3000 (backend server) with /api prefix
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const port = '3000'

      // If accessing via localhost, keep localhost (for development on same machine)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:${port}/api`
      }

      // For mobile or other devices, use the actual IP address with port 3000
      return `${protocol}//${hostname}:${port}/api`
    },

    isRawFile(file) {
      const ext = file.name.toLowerCase().split('.').pop()
      const rawExtensions = ['raw', 'nef', 'cr2', 'arw', 'dng', 'raf', 'orf', 'rw2', 'pef', 'srw']
      return rawExtensions.includes(ext)
    },

    getThumbnailUrl(filePath) {
      const baseUrl = this.getApiBaseUrl()
      return `${baseUrl}/thumbnail/${filePath}`
    },

    getThumbnailUrlWithAuth(filePath) {
      const baseUrl = this.getApiBaseUrl()
      const token = localStorage.getItem('authToken')
      return `${baseUrl}/thumbnail/${filePath}?token=${encodeURIComponent(token)}`
    },

    onThumbnailError(event, file) {
      // Hide broken image and show fallback icon
      event.target.style.display = 'none'
      const iconElement = event.target.nextElementSibling
      if (iconElement) {
        iconElement.style.display = 'inline-block'
      }
    },

    onThumbnailLoad(event) {
      // Show thumbnail and hide fallback icon
      event.target.style.display = 'block'
      const iconElement = event.target.nextElementSibling
      if (iconElement) {
        iconElement.style.display = 'none'
      }
    },
    
    getFileIcon(file) {
      if (file.type === 'folder') {
        return 'fas fa-folder'
      }
      
      const ext = file.name.toLowerCase().split('.').pop()
      const rawExtensions = ['raw', 'nef', 'cr2', 'arw', 'dng', 'raf', 'orf', 'rw2', 'pef', 'srw']
      
      if (rawExtensions.includes(ext)) {
        return 'fas fa-image'
      }
      
      return 'fas fa-file'
    },
    
    getFileIconClass(file) {
      if (file.type === 'folder') {
        return 'folder-icon'
      }
      
      const ext = file.name.toLowerCase().split('.').pop()
      const rawExtensions = ['raw', 'nef', 'cr2', 'arw', 'dng', 'raf', 'orf', 'rw2', 'pef', 'srw']
      
      if (rawExtensions.includes(ext)) {
        return 'image-icon'
      }
      
      return 'file-icon'
    }
  }
}
</script>

<style scoped>
.p-raw-body {
  max-height: 220px;
  overflow-y: scroll;
}

.raw-select-row {
  display: flex!important;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--surface-border);
  background: var(--surface-50);
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.file-icon-container {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.file-thumbnail-small {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f8f9fa;
}

.folder-icon {
  color: #ffc107;
}

.image-icon {
  color: #28a745;
}

.file-icon {
  color: #6c757d;
}
</style>